import { useCallback } from 'react';
import { Heading, Grid, Flex, Box, Button, Link, SkeletonText, Text } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { PageTitle } from '@/components/page-title';
import { generatePath } from '@/services/router/generate-path';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { CopilotInput, useCopilotSidebar } from '@/features/copilot';
import { useAuth } from '@/services/auth';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import { AiEnabledGuard } from '@/components/guards';
import { AccountState } from '@/services/auth/types';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { ActivityChart } from './components/activity-chart';
import { useGreeting } from '@/features/greeting';
import { LegacyDashboard } from './components/legacy-dashboard';
import { MessageInvadersCard } from './components/message-invaders';
import { PromptCards } from './components/prompt-cards';
import { RecentsRow, areRecentsStale } from './components/recents-row';
import { RecentlyLaunched } from './components/recently-launched';
import { SectionLabel } from './components/section-label';
import { WelcomeBanner } from './components/welcome-banner';
import { useRecents, useRecentlyShipped } from './hooks';
import * as styles from './index.css';

export function HomePage() {
  const { hello, tagline } = useGreeting();
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const { track } = useAnalytics();
  const { hasPermission } = usePermissions();
  const { account } = useAuth();
  const isWorkspaceSetupRedirect = useFeatureFlag(FeatureFlag.WorkspaceSetupRedirect);
  const isTrial = account.account_state === AccountState.Free;
  const canViewAuditLogs = hasPermission('account/edit');
  const canInviteTeammates = hasPermission('account/team_members/edit');
  const { data: recents, isLoading: recentsLoading } = useRecents(workspaceId);
  const { data: shipped, isLoading: shippedLoading } = useRecentlyShipped(workspaceId);

  const isRedirecting = isWorkspaceSetupRedirect && isTrial;

  usePageViewTracking(() => {
    track('home', 'viewed', {});
  }, !isRedirecting);

  const hasTeamActivity = shipped != null && shipped.length > 0;

  const { openCopilot } = useCopilotSidebar();

  const handleSubmit = useCallback(() => {
    track('home', 'agentInputSubmitted', {});
    openCopilot({ triggerSource: 'home_input' });
  }, [track, openCopilot]);

  if (isRedirecting) {
    return (
      <SafeNavigate
        to={generatePath('/workspaces/:workspaceId/journeys/home/workspace-setup', { workspaceId })}
        options={{ shouldReplaceHistoryEntry: true }}
      />
    );
  }

  return (
    <>
      <PageTitle title="Home" />
      <div className={styles.pageWrapper}>
        <header className={styles.pageHeader}>
          <Flex direction="column" gap="100">
            <Heading level="1" text="product-h1">
              {hello}
            </Heading>
            <Text text="product-p" color="subtle">
              {tagline}
            </Text>
          </Flex>
        </header>
        <Grid columns="1fr 320px" gap="400" alignItems="flex-start" className={styles.pageGrid}>
          <div className={styles.leftColumn}>
            <Box id="in-app-target_dashboard" className={styles.inAppTarget} />
            <Flex direction="column" gap="450" minW="0">
              <WelcomeBanner />
              <Box w="full">
                <Box mb="200">
                  <SectionLabel>
                    {!recentsLoading && (recents == null || recents.length === 0 || areRecentsStale(recents))
                      ? 'Time to start something new'
                      : 'Ready to keep going?'}
                  </SectionLabel>
                </Box>
                <RecentsRow recents={recents} isLoading={recentsLoading} workspaceId={workspaceId} />
              </Box>
              <ActivityChart workspaceId={workspaceId} isStale={!recentsLoading && areRecentsStale(recents)} />
              <AiEnabledGuard>
                <Flex direction="column" gap="150" w="full">
                  <PromptCards workspaceId={workspaceId} />
                  <Box w="full">
                    <CopilotInput onSubmit={handleSubmit} newSessionOnSubmit />
                  </Box>
                </Flex>
              </AiEnabledGuard>
            </Flex>
            <div className={styles.legacyDashboardFooter}>
              <LegacyDashboard />
            </div>
          </div>
          <Flex direction="column" gap="450" minW="0" overflow="hidden">
            <Box w="full">
              {shippedLoading ? (
                <>
                  <Box mb="200">
                    <SkeletonText lines={1} style={{ width: 160 }} />
                  </Box>
                  <RecentlyLaunched shipped={undefined} isLoading workspaceId={workspaceId} />
                </>
              ) : hasTeamActivity ? (
                <>
                  <Flex alignItems="center" justifyContent="space-between" mb="200">
                    <SectionLabel>What's your team up to?</SectionLabel>
                    {canViewAuditLogs ? (
                      <Text size="xs">
                        <Link
                          href={generatePath('/workspaces/:workspaceId/settings/audit-logs', { workspaceId })}
                          variant="primary"
                          icon="chevron-right"
                          iconPosition="trailing"
                          onClick={() => track('home', 'auditLogsClicked', {})}
                        >
                          Audit logs
                        </Link>
                      </Text>
                    ) : null}
                  </Flex>
                  <RecentlyLaunched shipped={shipped} isLoading={false} workspaceId={workspaceId} />
                </>
              ) : (
                <>
                  <Box mb="200">
                    <SectionLabel>Work better together</SectionLabel>
                  </Box>
                  <Text size="s" color="subtle">
                    Collaborate on campaigns and broadcasts — or challenge them to beat your Message Invaders high
                    score.
                  </Text>
                </>
              )}
              {canInviteTeammates ? (
                <Flex pt="200" justifyContent="center">
                  <Button
                    variant="secondary"
                    size="sm"
                    href="/settings/team/add"
                    icon="add"
                    onClick={() => track('home', 'inviteTeammateClicked', {})}
                  >
                    Invite teammates
                  </Button>
                </Flex>
              ) : null}
            </Box>
            <MessageInvadersCard />
          </Flex>
        </Grid>
      </div>
    </>
  );
}
